import { FontColor, H4Text } from '@prisjakt/ui/text/variants';
import React, { ReactNode, useEffect, useState } from 'react';
import { ScaleForm } from '../review-form';
import { Question, QuestionWrapper, Close, ScaleWrapper } from './styles';
import { IconClose } from '@prisjakt/ui/icon';
import { useAppContext } from '@prisjakt/ui-app-context';

export interface IQuestion {
  name: string;
  question: string;
  score: number;
}

export interface IOptions {
  value: number;
  label: string;
}

type Props = {
  questions: IQuestion[];
  options: IOptions[];
  onSubmit: (questions: IQuestion[]) => void;
  randomized?: boolean;
  submitOnAnswer?: boolean;
  thankYouScreen?: ReactNode;
};

export const Questions = ({
  questions,
  options,
  onSubmit,
  randomized = false,
  submitOnAnswer = false,
  thankYouScreen,
}: Props) => {
  const { t } = useAppContext();
  const [submitted, setSubmitted] = useState(false);
  const [answering, setAnswering] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [isOpen, setIsOpen] = useState(true);
  const [answeredQuestions, setAnsweredQuestions] = useState<IQuestion[]>([]);
  const [questionsOrder, setQuestionsOrder] = useState<IQuestion[]>([]);

  useEffect(() => {
    if (randomized) {
      const shuffledQuestions = questions
        .map(value => ({ value, sort: Math.random() }))
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);
      setQuestionsOrder(shuffledQuestions);
      return;
    }
    setQuestionsOrder(questions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = (questionsToSubmit: IQuestion[]) => {
    onSubmit(questionsToSubmit);
  };

  const handleSelectOption = (value: number) => {
    const nextStep = currentStep + 1;
    const isLastQuestion = nextStep === questions.length;
    const currentQuestion = questionsOrder[currentStep];
    currentQuestion.score = value;

    const questionsToSubmit = [currentQuestion, ...answeredQuestions];
    setAnsweredQuestions(questionsToSubmit);
    setAnswering(true);
    if (submitOnAnswer || isLastQuestion) {
      handleSubmit([currentQuestion]);
    }
    setTimeout(() => {
      if (isLastQuestion) {
        setSubmitted(true);
      }
      setCurrentStep(nextStep);
      setAnswering(false);
    }, 1000);
  };

  if (!isOpen) {
    return null;
  }

  if (submitted) {
    if (!thankYouScreen) {
      return null;
    }

    return (
      <QuestionWrapper>
        <Close onClick={() => setIsOpen(false)}>
          <IconClose />
        </Close>
        {thankYouScreen}
      </QuestionWrapper>
    );
  }

  if (!questionsOrder[currentStep]?.name) {
    return null;
  }

  return (
    <QuestionWrapper>
      <Question $fade={answering}>
        <div>
          <H4Text color={FontColor.PRIMARY}>
            {t('feedback', 'uup.question-title')}
          </H4Text>
          <H4Text>{questionsOrder[currentStep]?.question}</H4Text>
        </div>
        <ScaleWrapper>
          <ScaleForm
            disabled={answering}
            currentValue={null}
            name={questionsOrder[currentStep]?.name}
            options={options}
            onClick={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleSelectOption(parseInt(e.target.value, 10))
            }
          />
        </ScaleWrapper>
      </Question>
    </QuestionWrapper>
  );
};
